// Prayer Notebook Component - Person-based prayer organization like a prayer notebook

import { useState, useEffect, useMemo } from 'react';
import { prayersAPI } from '../api/client';
import { useAuth } from '../context/useAuth';
import type { StructuredPrayerSummary, PrayerSummaryItem } from '../types';
import {
  Heart,
  ChevronDown,
  ChevronRight,
  Users,
  Lock,
  Sparkles,
  Loader2,
  User as UserIcon,
  BookOpen,
} from 'lucide-react';

interface PersonSection {
  id: string;
  name: string;
  isCurrentUser: boolean;
  userId: number | null;
  prayers: PrayerSummaryItem[];
  categories: { key: string; label: string; prayers: PrayerSummaryItem[] }[];
}

interface PrayerNotebookProps {
  /** When this value changes, the notebook refetches (e.g. after a prayer is created). */
  refreshTrigger?: number;
}

export default function PrayerNotebook({ refreshTrigger }: PrayerNotebookProps) {
  const { user } = useAuth();
  const [data, setData] = useState<StructuredPrayerSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPersons, setExpandedPersons] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadData();
  }, [refreshTrigger]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      setData(await prayersAPI.getStructuredSummary());
    } catch (err) {
      console.error('Error loading prayer notebook:', err);
      setError('Unable to load prayers');
    } finally {
      setIsLoading(false);
    }
  };

  const togglePerson = (personId: string) => {
    setExpandedPersons(prev => {
      const next = new Set(prev);
      if (next.has(personId)) {
        next.delete(personId);
      } else {
        next.add(personId);
      }
      return next;
    });
  };

  const getCategoryIcon = (key: string) => {
    const icons: Record<string, string> = {
      family: '👨‍👩‍👧‍👦',
      health: '🏥',
      work: '💼',
      finances: '💰',
      spiritual: '✝️',
      relationships: '🤝',
      other: '📝',
    };
    return icons[key] || '📝';
  };

  const getCategoryLabel = (key: string) => {
    const labels: Record<string, string> = {
      family: 'Family',
      health: 'Health',
      work: 'Work/Career',
      finances: 'Finances',
      spiritual: 'Spiritual Growth',
      relationships: 'Relationships',
      other: 'Other',
    };
    return labels[key] || key;
  };

  // Compute person sections - group prayers by who they're praying for.
  // Only people with at least one active prayer get a section.
  const personSections = useMemo((): PersonSection[] => {
    if (!data || !user) return [];

    // Flatten all prayers from categories
    const allPrayers: PrayerSummaryItem[] = data.categories.flatMap(cat =>
      cat.prayers.map(p => ({ ...p, category: cat.key, category_display: cat.label }))
    );

    // Group prayers by praying_for_display
    const prayersByPerson: Map<string, { userId: number | null; prayers: PrayerSummaryItem[] }> = new Map();

    for (const prayer of allPrayers) {
      // Determine who this prayer is for
      let personKey: string;
      let personUserId: number | null = null;

      if (prayer.praying_for_user_id === user.id) {
        // Prayers for the current user go to "My Prayers"
        personKey = '__my_prayers__';
        personUserId = user.id;
      } else if (prayer.praying_for_display) {
        // Use the praying_for_display field for others
        personKey = prayer.praying_for_display;
        personUserId = prayer.praying_for_user_id || null;
      } else if (prayer.is_own) {
        // Own prayers without praying_for go to "My Prayers"
        personKey = '__my_prayers__';
        personUserId = user.id;
      } else {
        // Group prayers use the prayer author's name
        personKey = prayer.user_name;
        personUserId = null;
      }

      if (!prayersByPerson.has(personKey)) {
        prayersByPerson.set(personKey, { userId: personUserId, prayers: [] });
      }
      prayersByPerson.get(personKey)!.prayers.push(prayer);
    }

    // Convert to PersonSection array
    const sections: PersonSection[] = [];

    // My Prayers first
    const myPrayers = prayersByPerson.get('__my_prayers__');
    if (myPrayers) {
      const myCategories = groupByCategory(myPrayers.prayers);
      sections.push({
        id: '__my_prayers__',
        name: 'My Prayers',
        isCurrentUser: true,
        userId: user.id,
        prayers: myPrayers.prayers,
        categories: myCategories,
      });
      prayersByPerson.delete('__my_prayers__');
    }

    // Other people alphabetically
    const sortedPeople = Array.from(prayersByPerson.entries())
      .sort(([a], [b]) => a.localeCompare(b));

    for (const [name, data] of sortedPeople) {
      const categories = groupByCategory(data.prayers);
      sections.push({
        id: `person_${data.userId || name}`,
        name,
        isCurrentUser: false,
        userId: data.userId,
        prayers: data.prayers,
        categories,
      });
    }

    return sections;
  }, [data, user]);

  // Initialize expanded state after computing sections
  useEffect(() => {
    if (personSections.length > 0 && expandedPersons.size === 0) {
      setExpandedPersons(new Set(personSections.map(s => s.id)));
    }
  }, [personSections]);

  function groupByCategory(prayers: PrayerSummaryItem[]): { key: string; label: string; prayers: PrayerSummaryItem[] }[] {
    const categoryMap = new Map<string, PrayerSummaryItem[]>();

    for (const prayer of prayers) {
      const key = prayer.category || 'other';
      if (!categoryMap.has(key)) {
        categoryMap.set(key, []);
      }
      categoryMap.get(key)!.push(prayer);
    }

    // Sort by category order
    const categoryOrder = ['family', 'health', 'work', 'finances', 'spiritual', 'relationships', 'other'];
    return categoryOrder
      .filter(key => categoryMap.has(key))
      .map(key => ({
        key,
        label: getCategoryLabel(key),
        prayers: categoryMap.get(key)!,
      }));
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="animate-spin text-navy" size={32} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="text-center py-8">
          <p className="text-gray-500">{error}</p>
          <button
            onClick={loadData}
            className="mt-2 text-gold hover:underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const totalPrayers = personSections.reduce((sum, s) => sum + s.prayers.length, 0);
  const newPrayers = personSections.reduce(
    (sum, s) => sum + s.prayers.filter(p => p.is_new).length,
    0
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="text-navy" size={24} />
            <h2 className="text-lg font-semibold text-navy">Prayer Notebook</h2>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 bg-navy rounded-full" />
              <span className="text-gray-600">
                <span className="font-semibold text-gray-800">{totalPrayers}</span> Active
              </span>
            </div>
            {newPrayers > 0 && (
              <div className="flex items-center gap-1.5">
                <Sparkles size={14} className="text-gold" />
                <span className="text-gray-600">
                  <span className="font-semibold text-gray-800">{newPrayers}</span> New
                </span>
              </div>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Organize your prayers by who you're praying for
        </p>
      </div>

      {/* Person Sections */}
      {personSections.length === 0 ? (
        <div className="p-6 text-center">
          <Heart className="mx-auto text-gray-300 mb-2" size={32} />
          <p className="text-gray-500">No prayer requests yet</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {personSections.map((person) => (
            <div key={person.id}>
              {/* Person Header */}
              <button
                onClick={() => togglePerson(person.id)}
                aria-expanded={expandedPersons.has(person.id)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    person.isCurrentUser 
                      ? 'bg-navy text-white' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {person.isCurrentUser ? (
                      <Heart size={18} />
                    ) : (
                      <UserIcon size={18} />
                    )}
                  </div>
                  <div className="text-left">
                    <span className="font-medium text-gray-800">{person.name}</span>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <span>{person.prayers.length} prayer{person.prayers.length !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </div>
                {expandedPersons.has(person.id) ? (
                  <ChevronDown size={18} className="text-gray-400" />
                ) : (
                  <ChevronRight size={18} className="text-gray-400" />
                )}
              </button>

              {/* Person's Prayers by Category */}
              {expandedPersons.has(person.id) && person.categories.length > 0 && (
                <div className="bg-gray-50/50 pb-2">
                  {person.categories.map((category) => (
                    <div key={`${person.id}-${category.key}`} className="ml-6 mr-4">
                      {/* Category Sub-header */}
                      <div className="flex items-center gap-2 py-2 text-sm text-gray-500">
                        <span>{getCategoryIcon(category.key)}</span>
                        <span className="font-medium">{category.label}</span>
                        <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full">
                          {category.prayers.length}
                        </span>
                      </div>

                      {/* Prayer Items */}
                      <div className="space-y-2 ml-6 mb-3">
                        {category.prayers.map((prayer) => (
                          <div
                            key={prayer.id}
                            className={`p-3 bg-white rounded-lg border border-gray-100 ${
                              prayer.is_new ? 'ring-1 ring-gold/30' : ''
                            }`}
                          >
                            {/* Title with badges */}
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-gray-800">
                                {prayer.title}
                              </span>
                              {prayer.is_new && (
                                <span className="text-xs bg-gold/10 text-gold px-1.5 py-0.5 rounded">
                                  New
                                </span>
                              )}
                              {prayer.is_private && (
                                <Lock size={12} className="text-gray-400" />
                              )}
                            </div>

                            {/* Source info */}
                            {!person.isCurrentUser && (
                              <div className="flex items-center gap-1 mt-0.5 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Users size={12} />
                                  {prayer.user_name}
                                </span>
                                {prayer.source === 'snapshot' && prayer.snapshot_month && (
                                  <span className="text-gray-400">
                                    · {prayer.snapshot_month}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Content preview */}
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                              {prayer.content}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
